import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconTrash } from '../components/icons';

interface FinancialYear {
  id: number;
  name: string;
}

interface OrgUnit {
  id: number;
  unit_name: string;
}

interface ChartOfAccount {
  id: number;
  code: string;
  name: string;
}

interface LineForm {
  org_unit_id: number | '';
  chart_of_account_id: number | '';
  type: 'operational' | 'capital';
  project_name: string;
  amount: string;
  notes: string;
}

const emptyLine: LineForm = { org_unit_id: '', chart_of_account_id: '', type: 'operational', project_name: '', amount: '', notes: '' };

interface BudgetLoaded {
  financial_year_id: number;
  status: string;
  lines: {
    org_unit_id: number | null;
    chart_of_account_id: number;
    type: string;
    project_name: string | null;
    amount: string;
    notes: string | null;
  }[];
}

export function BudgetForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [financialYears, setFinancialYears] = useState<FinancialYear[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([]);
  const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccount[]>([]);
  const [financialYearId, setFinancialYearId] = useState<number | ''>('');
  const [lines, setLines] = useState<LineForm[]>([{ ...emptyLine }]);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    api.get<FinancialYear[]>('/financial-years').then((res) => setFinancialYears(res.data));
    api.get<OrgUnit[]>('/org-units').then((res) => setOrgUnits(res.data));
    api.get<ChartOfAccount[]>('/chart-of-accounts').then((res) => setChartOfAccounts(res.data));
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get<BudgetLoaded>(`/budgets/${id}`).then((res) => {
      setFinancialYearId(res.data.financial_year_id);
      setLines(
        res.data.lines.map((l) => ({
          org_unit_id: l.org_unit_id ?? '',
          chart_of_account_id: l.chart_of_account_id,
          type: l.type === 'capital' ? 'capital' : 'operational',
          project_name: l.project_name ?? '',
          amount: l.amount,
          notes: l.notes ?? '',
        }))
      );
      setLoaded(true);
    });
  }, [id]);

  const total = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);

  const updateLine = (index: number, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, { ...emptyLine }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const payload = {
      financial_year_id: financialYearId,
      lines: lines
        .filter((l) => l.chart_of_account_id)
        .map((l) => ({
          org_unit_id: l.org_unit_id || null,
          chart_of_account_id: l.chart_of_account_id,
          type: l.type,
          project_name: l.type === 'capital' ? l.project_name || null : null,
          amount: l.amount || 0,
          notes: l.notes || null,
        })),
    };
    try {
      if (id) {
        await api.put(`/budgets/${id}`, payload);
        navigate(`/budgets/${id}`);
      } else {
        const res = await api.post<{ id: number }>('/budgets', payload);
        navigate(`/budgets/${res.data.id}`);
      }
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back" onClick={() => navigate(-1)} />
          <h1>{id ? 'Edit budget' : 'New budget'}</h1>
        </div>
      </header>

      <form onSubmit={submit} className="card">
        {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
        <div className="form-grid">
          <label>
            Financial year *
            <select
              value={financialYearId}
              onChange={(e) => setFinancialYearId(e.target.value ? Number(e.target.value) : '')}
              required
              disabled={!!id}
            >
              <option value="">Select…</option>
              {financialYears.map((fy) => (
                <option key={fy.id} value={fy.id}>
                  {fy.name}
                </option>
              ))}
            </select>
            {errors.financial_year_id && <small className="error">{errors.financial_year_id[0]}</small>}
          </label>
        </div>

        <h2>Lines</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Cost Centre</th>
                <th>Account</th>
                <th>Type</th>
                <th>Project</th>
                <th>Amount</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td>
                    <select
                      value={line.org_unit_id}
                      onChange={(e) => updateLine(i, { org_unit_id: e.target.value ? Number(e.target.value) : '' })}
                    >
                      <option value="">Fund-wide</option>
                      {orgUnits.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.unit_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={line.chart_of_account_id}
                      onChange={(e) => updateLine(i, { chart_of_account_id: e.target.value ? Number(e.target.value) : '' })}
                      required
                    >
                      <option value="">Select…</option>
                      {chartOfAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={line.type}
                      onChange={(e) => updateLine(i, { type: e.target.value as 'operational' | 'capital' })}
                    >
                      <option value="operational">Operational</option>
                      <option value="capital">Capital</option>
                    </select>
                  </td>
                  <td>
                    <input
                      value={line.project_name}
                      onChange={(e) => updateLine(i, { project_name: e.target.value })}
                      disabled={line.type !== 'capital'}
                      placeholder={line.type === 'capital' ? 'e.g. Head office roof replacement' : undefined}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.amount}
                      onChange={(e) => updateLine(i, { amount: e.target.value })}
                      required
                    />
                  </td>
                  <td>
                    <input value={line.notes} onChange={(e) => updateLine(i, { notes: e.target.value })} />
                  </td>
                  <td>
                    <IconButton
                      icon={IconTrash}
                      label="Remove line"
                      variant="danger-ghost"
                      onClick={() => removeLine(i)}
                      disabled={lines.length <= 1}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="right">
                  <strong>Total</strong>
                </td>
                <td colSpan={3}>
                  <strong>{total.toFixed(2)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {errors.lines && <small className="error">{errors.lines[0]}</small>}

        <div className="toolbar">
          <button type="button" className="btn btn-ghost" onClick={addLine}>
            + Add Line
          </button>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Draft'}
          </button>
        </div>
      </form>
    </>
  );
}
